import { LucideIcon, SearchIcon } from "lucide-react";
import { UseFormRegisterReturn } from "react-hook-form";

import FormWrapper from "./FormWrapper";

import AdornmentInput from "@/components/ui/adornment-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/input-helper";

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  type?:
    | "text"
    | "email"
    | "password"
    | "number"
    | "date"
    | "time"
    | "file"
    | "input-search"
    | "adornment";
};
type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  type: "textarea";
};

type CommonProps = {
  name: string;
  placeholder?: string;
  className?: string;
  label?: string;
  error?: string;
  required?: boolean;
  register?: UseFormRegisterReturn;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  step?: number;
  onChange?: (e: React.WheelEventHandler<HTMLInputElement>) => void;
  labelWrapperClassName?: string;
};

type IProps = CommonProps & (InputProps | TextareaProps);

type TInputFieldWrapperProps = Pick<
  IProps,
  "name" | "label" | "error" | "required" | "className"
> & {
  children: React.ReactNode;
};

export const InputFieldWrapper: React.FC<TInputFieldWrapperProps> = ({
  name,
  label,
  error,
  required,
  className,
  children,
}) => {
  return (
    <fieldset className={cn("flex flex-col gap-y-1", className)}>
      {label ? (
        <Label
          htmlFor={name}
          className='text-sm font-normal flex items-start gap-x-[0.5px]'
          required={required}
        >
          {label}
        </Label>
      ) : null}

      <div>{children}</div>

      {error && <p className='text-xs text-red-500'>{error}</p>}
    </fieldset>
  );
};

export type TInputFieldProps = Omit<IProps, "name"> & {
  name?: string;
  hideZero?: boolean;
  icon?: {
    name: LucideIcon;
    isBackIcon?: boolean;
    iconClassName?: string;
  };
  rows?: number;
};

export const InputField: React.FC<TInputFieldProps> = ({
  name,
  label,
  placeholder,
  type,
  required,
  error,
  className,
  disabled,
  icon,
  hideZero = true,
  ...props
}) => {
  const getField = (): React.JSX.Element => {
    switch (type) {
      case "textarea":
        return (
          <Textarea
            id={props.register?.name || name}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(error && "border-red-500")}
            rows={props?.rows || 5}
            {...props.register}
            {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        );
      case "input-search":
        return (
          <div
            className={
              "border md:flex md:self-center h-10 items-center rounded-md pl-3 text-sm "
            }
          >
            <SearchIcon className='h-[20px] w-[20px]' />
            <Input
              id={props.register?.name || name}
              type={type || "text"}
              placeholder={cn(placeholder)}
              className='w-full p-2 border-0 placeholder:opacity-80 border-y focus-visible:ring-0 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
              {...(icon && {
                icon: {
                  icon: icon?.name,
                  position: icon?.isBackIcon ? "right" : "left",
                  className: icon?.iconClassName,
                },
              })}
              {...props.register}
              {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
            />
          </div>
        );
      case "adornment":
        return (
          <AdornmentInput
            id={props.register?.name || name}
            type={"number"}
            placeholder={cn(placeholder)}
            pattern='d*'
            className={cn(
              "w-full p-2 placeholder:opacity-80  rounded-l-none",
              className,
            )}
            {...props.register}
            {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
            onWheel={(e): void => {
              e.currentTarget.blur();
            }}
            onKeyDown={formatNumber}
          />
        );
      default:
        return (
          <Input
            id={props.register?.name || name}
            disabled={disabled}
            type={type || "text"}
            pattern={type === "number" ? "d*" : ".*"}
            placeholder={placeholder}
            {...(icon && {
              icon: {
                icon: icon?.name,
                position: icon?.isBackIcon ? "right" : "left",
                className: icon?.iconClassName,
              },
            })}
            // className={cn(error && 'border-red-500')}
            {...props.register}
            {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
            {...(type === "number" && {
              onKeyDown: formatNumber,
              onWheel: (e): void => {
                e.currentTarget.blur();
              },
            })}
            value={props.value === 0 && hideZero ? "" : props.value}
          />
        );
    }
  };

  return (
    <FormWrapper
      label={label}
      isRequired={required}
      className={className}
      labelWrapperClassName={props.labelWrapperClassName}
    >
      {getField()}
    </FormWrapper>
  );
};

export default InputField;
